import { useEffect, type ReactNode } from 'react';
import { SiteFrame } from '../components/SiteFrame.jsx';
import { useViewer } from '../lib/viewer.js';

/**
 * The privacy policy and the terms, at `/privacy` and `/terms`.
 *
 * Two plain pages, written in the same voice as the ones for plus2 — short,
 * direct, no fine print that says otherwise. Discord will not register a bot
 * without both, and a reviewer reads them, so they describe what tomo
 * ACTUALLY keeps. Changing what is stored is a change to this file too: a
 * policy that drifts from the code is a promise nobody is keeping.
 *
 * Served by the console bundle rather than as loose HTML so they share the
 * landing's type and tokens. Neither needs a session.
 */

export type LegalPage = 'privacy' | 'terms';

const UPDATED = 'September 25, 2026';

/** Which legal page this path is, or null. A trailing slash is the same page. */
export function legalPageFor(pathname: string): LegalPage | null {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  return null;
}

export function Legal({ page }: { page: LegalPage }): ReactNode {
  const viewer = useViewer();
  useEffect(() => {
    document.title = page === 'privacy' ? 'Privacy · tomo' : 'Terms of Service · tomo';
  }, [page]);

  const title = page === 'privacy' ? 'Privacy' : 'Terms of Service';
  return (
    <SiteFrame viewer={viewer} trail={[{ label: title }]}>
      {/* A reading measure, inside the one frame — see SiteFrame. */}
      <div className="flex max-w-2xl flex-col gap-8">
        <header>
          <h1 className="text-2xl font-bold tracking-tight text-ink-50">{title}</h1>
          <p className="mt-1 text-xs text-ink-700">Last updated {UPDATED}</p>
        </header>

        <article className="flex flex-col gap-6 text-[15px] leading-relaxed text-ink-200">
          {page === 'privacy' ? <Privacy /> : <Terms />}
        </article>
      </div>
    </SiteFrame>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-ink-50">{title}</h2>
      {children}
    </section>
  );
}

function List({ children }: { children: ReactNode }): ReactNode {
  return <ul className="flex list-disc flex-col gap-1.5 pl-5 marker:text-ink-700">{children}</ul>;
}

function Contact(): ReactNode {
  return <p className="font-medium text-ink-50">tyler at awful fun</p>;
}

function Privacy(): ReactNode {
  return (
    <>
      <p className="text-lg font-semibold text-ink-50">
        We don&rsquo;t sell your data. We never have, and we never will.
      </p>
      <p>
        Honestly, we don&rsquo;t care about your data. It&rsquo;s yours. We&rsquo;re not in the data
        business. We keep what tomo needs to work, and this page is the whole list.
      </p>
      <p>
        No trackers. No ads. No shady third-party deals. No fine print that says otherwise.
      </p>

      <Section title="When you sign in with Twitch">
        <List>
          <li>Your Twitch id, username, display name and profile picture, so we know who you are.</li>
          <li>
            A sign-in token from Twitch, kept encrypted, so tomo can show you chat and send your
            messages as you, offer the emotes you can use, and show you who you follow. You can cut
            it off any time at twitch.tv &rarr; Settings &rarr; Connections.
          </li>
          <li>Twitch shows us your email address when you sign in. We don&rsquo;t keep it.</li>
          <li>One cookie, to keep you signed in. That&rsquo;s the only one.</li>
        </List>
      </Section>

      <Section title="When you use a room">
        <List>
          <li>Messages you post in room chat, so everyone in the room sees the same thing. You can delete your own.</li>
          <li>Rooms you make, and people you block.</li>
          <li>
            That you&rsquo;re in a room, while you&rsquo;re in it. Sharing what you&rsquo;re watching is off
            unless you turn it on.
          </li>
          <li>Room layout and settings stay on your device. We never see them.</li>
        </List>
        <p>
          Twitch chat you read in a room comes straight from Twitch to your browser. We don&rsquo;t
          store it today. We are thinking about keeping recent chat for the communities that want it,
          so you can scroll back past when you showed up. If we do, it will be opt-in per community,
          kept for a limited time, and this page will say so before it starts.
        </p>
      </Section>

      <Section title="When a Discord server uses the tomo bot">
        <List>
          <li>The server&rsquo;s id and name.</li>
          <li>The channels and roles a server admin picks for announcements.</li>
          <li>Which communities the server follows.</li>
          <li>Who a server admin allows to send announcements, as Discord user or role ids.</li>
          <li>The announcements that go out: what, who sent it, and when.</li>
        </List>
        <p>
          The bot doesn&rsquo;t read messages in your server. It doesn&rsquo;t listen to your server at all.
          It only posts when something goes live or someone sends an announcement, and answers
          commands you give it. Remove the bot and we delete that server&rsquo;s settings.
        </p>
      </Section>

      <Section title="When you report a bug">
        <p>
          What you write, the page you were on, your browser, and a screenshot if you attach one. We
          use it to fix the thing, and nothing else.
        </p>
      </Section>

      <Section title="Who else is involved">
        <p>
          tomo runs on Google Cloud, which stores all of the above. Twitch and Discord see what they
          already see when you use their services through us. That&rsquo;s it. Nobody else gets anything.
        </p>
        <p>The only time we&rsquo;d ever look at your data is if something breaks and we need to figure out why.</p>
      </Section>

      <Section title="Deleting your stuff">
        <p>
          Ask and we&rsquo;ll delete your account and everything tied to it. Same address as below.
        </p>
      </Section>

      <Section title="Kids">
        <p>tomo is for people 13 and older, same as Twitch and Discord.</p>
      </Section>

      <Section title="Changes">
        <p>
          If what we keep changes, this page changes first. If it&rsquo;s a big change, we&rsquo;ll make it
          obvious.
        </p>
      </Section>

      <Section title="Questions">
        <p>Reach out. We&rsquo;ll give you a straight answer:</p>
        <Contact />
      </Section>
    </>
  );
}

function Terms(): ReactNode {
  return (
    <>
      <p className="text-lg font-semibold text-ink-50">
        Welcome! By using our stuff, you agree to be cool about it and we&rsquo;ll do the same.
      </p>

      <Section title="Using the service">
        <p>
          Use of tomo (rooms, communities, the tomo Discord bot, and whatever else we build on
          tomo.channel, our stuff) should be for its intended purpose. Don&rsquo;t break things,
          don&rsquo;t abuse the platform, and don&rsquo;t use it to do anything illegal. Please.
        </p>
        <p>
          You sign in with Twitch and the bot lives in Discord, so their rules apply too. Follow the
          Twitch Terms of Service and Community Guidelines, and Discord&rsquo;s Terms of Service and
          Community Guidelines. tomo isn&rsquo;t made by, or affiliated with, Twitch or Discord.
        </p>
        <p>
          We reserve the right to suspend or remove accounts, rooms or servers that violate these terms
          or are generally being terrible.
        </p>
      </Section>

      <Section title="Rooms and chat">
        <p>
          Be decent in room chat. Room owners can mute and kick people in their rooms, and we can ban
          people from tomo. Twitch chat shown in a room belongs to that streamer&rsquo;s channel and is
          moderated there.
        </p>
      </Section>

      <Section title="The Discord bot">
        <p>
          Server admins decide which communities the bot follows, where it posts, and who may send
          announcements. If you&rsquo;re allowed to announce, what you send is on you: no spam, no
          pinging everyone for things that aren&rsquo;t worth it, nothing you couldn&rsquo;t post in that
          server yourself. Admins can change or remove any of it, and kicking the bot turns it all off.
        </p>
      </Section>

      <Section title="Your stuff">
        <p>
          Anything you create, upload, or share through our stuff is yours. We don&rsquo;t claim ownership
          of your content. We only access it as described in our{' '}
          <a href="/privacy" className="text-brand-300 underline-offset-2 hover:underline">Privacy policy</a>.
          Basically, just to keep things running.
        </p>
      </Section>

      <Section title="Availability">
        <p>
          We do our best to keep things up and running, but we can&rsquo;t guarantee 100% uptime.
          Sometimes stuff breaks, and sometimes Twitch or Discord do. We&rsquo;ll fix our part as fast as
          we can.
        </p>
      </Section>

      <Section title="Changes">
        <p>
          We might update these terms from time to time. If we make significant changes, we&rsquo;ll make
          it obvious. Continued use after changes means you&rsquo;re cool with the new terms.
        </p>
      </Section>

      <Section title="Liability">
        <p>
          Our stuff is provided as-is. We&rsquo;re not liable for any damages that come from using (or not
          being able to use) the service. We care about building fun, but we can&rsquo;t promise perfection.
        </p>
      </Section>

      <Section title="Contact">
        <p>Got questions or concerns? Reach out and we&rsquo;ll sort it out:</p>
        <Contact />
      </Section>
    </>
  );
}
